import React, { useCallback, useMemo, useState } from "react";

import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import BinIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InsertPageBreakIcon from "@mui/icons-material/InsertPageBreak";
import { IconButton, Menu, MenuItem, Stack, TableCell, TableCellProps, TableRow, Typography } from "@mui/material";
import { ExtratableRepoIcon } from "assets/material-custom-icons";
import { DataExtractionModal } from "components/Data-extraction/DataExtractionModal";
import { RoleComponentGuard } from "components/guards";
import { ActionRoleGuard } from "components/guards/Action-role.guard";
import { ReportButton } from "components/reports";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { usePrompt } from "hooks";
import { usePickUserPermissions } from "store";
import { GetSplitDetailsReq, IDeal, MeasureDataTableRow, MonitoringDataFiles, Report } from "store/types";

import { FileType, RoleName } from "../../../constants";

const batchIdKey = "batch_id";
// const enteredByKey = "entered_by";
const updatedByKey = "updated_by";

interface Props {
  row: MeasureDataTableRow;
  dealId?: IDeal["id"];
  inspectRow: (id: number) => void;
  deleteRow: (id: number, isGroup?: boolean) => void;
  hideChildCell?: boolean;
  borderLess?: boolean;
  columns: (keyof MeasureDataTableRow)[];
  inspectGroup: (data: GetSplitDetailsReq) => Promise<void>;
  onFileClick: (file: { file_url: string; type: FileType; name: string }) => void;
  getReportsByGroup: (groupName: string) => Report[];
}

export const Row: React.FC<Props> = ({ row, onFileClick, ...restProps }) => {
  const { deleted_at, children, id } = row;
  const {
    // setMonitoringSaveOne,
    inspectRow,
    deleteRow,
    borderLess = false,
    columns = [],
    inspectGroup
  } = restProps;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filesAnchorEl, setFilesAnchorEl] = useState<null | HTMLElement>(null);
  // const { errorModal } = useModals();
  const openPrompt = usePrompt();
  const dealMetrics = usePickUserPermissions("deal_metrics");
  const isViewerPermission = dealMetrics && dealMetrics === "viewer";

  const handleDelete = useCallback(() => {
    if (children?.length) {
      return deleteRow(children[0].id, true);
    }
    deleteRow(id);
  }, [children, deleteRow, id]);

  const border = useMemo(() => {
    const style = "0.5px solid #dae0e2";
    if (children?.length && isOpen) return "none";
    if (borderLess) return "none";
    return style;
  }, [children, isOpen, borderLess]);

  const commonCellStyles: TableCellProps["sx"] = {
    borderBottom: border,
    fontSize: "15px",
    color: deleted_at ? "#0a314280" : "#013951",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    maxWidth: "250px",
    backgroundColor: "transparent",
    textDecoration: deleted_at ? "line-through" : "none",
    height: "50px"
  };

  return (
    <>
      <TableRow
        sx={{
          transition: "background-color 0.2s linear",
          ".hiddable": {
            transition: "all 0.2s linear",
            opacity: 0
          },
          ":hover": {
            ".hiddable": {
              opacity: 1
            }
          }
        }}
      >
        <TableCell sx={commonCellStyles}>
          {!!children?.length && (
            <IconButton
              sx={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)" }}
              onClick={() => setIsOpen(!isOpen)}
              size="sm"
            >
              <ArrowForwardIosOutlinedIcon sx={{ fontSize: "16px", color: "#0a3142" }} />
            </IconButton>
          )}
        </TableCell>
        {columns.map((key, index) => (
          <TableCell
            key={key}
            align={!index ? "left" : "center"}
            sx={{ ...commonCellStyles, cursor: key === batchIdKey ? "pointer" : "default" }}
          >
            <ActionRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
              <MaterialTooltip isShow placement="top" title={row[key] as string}>
                <div
                  onClick={() => {
                    if (key !== batchIdKey) return;
                    if (!row.batch_id) return;
                    inspectGroup({ deal_id: row.parent_id || row.deal_id, batch_id: row.batch_id });
                  }}
                  style={{ cursor: key === batchIdKey ? "pointer" : "default" }}
                >
                  <Stack>
                    {key === updatedByKey && row.is_ai ? (
                      <Stack direction="row" alignItems="center" gap="4px">
                        <AutoAwesomeOutlinedIcon sx={{ fontSize: 16, color: "#c09560" }} />
                        <Typography sx={{ fontSize: "15px", fontWeight: 400 }}>Added by AI</Typography>
                      </Stack>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: "15px",
                          color: deleted_at ? "#0a314280" : "#013951",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden"
                        }}
                      >
                        {row[key] as string}
                      </Typography>
                    )}
                    {key === updatedByKey && row.updated_at && (
                      <Typography
                        sx={{
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden"
                        }}
                      >
                        On: {row.updated_at}
                      </Typography>
                    )}
                  </Stack>
                </div>
              </MaterialTooltip>
            </ActionRoleGuard>
          </TableCell>
        ))}
        <TableCell sx={commonCellStyles}>
          {!deleted_at && (
            <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isViewerPermission}>
              <Stack direction="row">
                <ReportButton
                  group="measure_data"
                  context={{
                    measure_data_id: row.id,
                    batch_id: row.batch_id,
                    deal_id: row.deal_id,
                    parent_id: row.parent_id,
                    is_child: restProps.hideChildCell === true,
                    children_count: row.children?.length || 0,
                    row: { ...row }
                  }}
                  icon={<InsertPageBreakIcon sx={{ fontSize: "16px" }} />}
                  buttonProps={{ size: "small", className: "hiddable" }}
                  getReportsByGroup={restProps.getReportsByGroup}
                />
                {!deleted_at && (
                  <IconButton className="hiddable" size="sm" onClick={handleDelete}>
                    <BinIcon />
                  </IconButton>
                )}
                {!deleted_at && (
                  <IconButton
                    className="hiddable"
                    onClick={() =>
                      children?.length && row.split_id
                        ? inspectGroup({ deal_id: row.deal_id, split_id: row.split_id })
                        : inspectRow(id)
                    }
                    size="sm"
                  >
                    <CreateOutlinedIcon />
                  </IconButton>
                )}
                {!deleted_at && Boolean(row.files?.length) && (
                  <>
                    <Stack className="hiddable" direction="row" gap="5px" alignItems="center">
                      <IconButton onClick={(event) => setFilesAnchorEl(event.currentTarget)} size="sm">
                        <DescriptionOutlinedIcon sx={{ color: "#26A6AE" }} />
                      </IconButton>

                      <Typography color="#26A6AE">+{row.files?.length}</Typography>
                    </Stack>
                    <Menu
                      sx={{ p: "10px" }}
                      anchorEl={filesAnchorEl}
                      open={Boolean(filesAnchorEl)}
                      onClose={() => setFilesAnchorEl(null)}
                    >
                      {row.files?.map((file: MonitoringDataFiles) => {
                        const isPdf = file.name.toLowerCase().endsWith(".pdf");

                        return (
                          <MaterialTooltip
                            key={file.id}
                            title={isPdf ? file.name : "Unsupported file format — click to download"}
                            isShow
                            variant="dark"
                          >
                            <MenuItem
                              onClick={async () => {
                                setFilesAnchorEl(null);
                                if (isPdf) {
                                  await openPrompt({
                                    componentType: "fullscreen",
                                    Component: DataExtractionModal,
                                    props: { fileIds: [file.id], fileNames: { [file.id]: file.name } }
                                  });
                                } else {
                                  onFileClick({ file_url: file.file_url, type: FileType.PDF, name: file.name });
                                }
                              }}
                            >
                              <Stack direction="row" gap="10px">
                                <DescriptionOutlinedIcon />
                                <Typography
                                  sx={{
                                    maxWidth: "150px",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                  }}
                                >
                                  {file.name}
                                </Typography>
                              </Stack>
                            </MenuItem>
                          </MaterialTooltip>
                        );
                      })}
                    </Menu>
                  </>
                )}
              </Stack>
            </RoleComponentGuard>
          )}
        </TableCell>
        <TableCell sx={commonCellStyles} align="center">
          <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
            {!!row.tags?.length && (
              <MaterialTooltip
                isShow
                title={
                  <>
                    Open <strong>{row.tags.map((tag) => tag.file_name).join(", ")}</strong> in Data Extractor
                  </>
                }
                placement="top"
              >
                <IconButton
                  onClick={async () => {
                    if (!row.tags) return;
                    const fileIds = Array.from(new Set(row.tags.map((tag) => tag.file_id)));
                    const fileNames = row.tags.reduce<Record<number, string>>((acc, tag) => {
                      acc[tag.file_id] = tag.file_name;
                      return acc;
                    }, {});
                    await openPrompt({
                      componentType: "fullscreen",
                      Component: DataExtractionModal,
                      props: { fileIds, fileNames }
                    });
                  }}
                >
                  <ExtratableRepoIcon sx={{ width: 20, height: 16, fill: "#0a3142" }} />
                </IconButton>
              </MaterialTooltip>
            )}
          </RoleComponentGuard>
        </TableCell>
      </TableRow>
      {isOpen &&
        row?.children?.map((row, index, arr) => (
          <Row
            key={id || index}
            {...restProps}
            row={row}
            hideChildCell
            borderLess={index < arr.length - 1}
            onFileClick={onFileClick}
          />
        ))}
    </>
  );
};
